import React, { ReactElement, useEffect, useState } from "react";
import { FiTrash2 } from "react-icons/fi";
import classNames from "classnames";
import {
    Comparison, FieldTypedValue, FormField, FormFieldType, WorkflowCondition,
} from "../../../__generated__/api";
import FieldTypeValueDisplay from "../../form-field/FieldTypeValueDisplay";
import FieldSelector from "../../pickers/field-selector/FieldSelector";
import Select from "../../select/Select";
import { ActionProps } from "./base";
import { useTheme } from "../../../contexts/Theme";
import Styles from "./condition.module.scss";
import Button from "../../theme/button/Button";

const FORM_FIELD_ID = "urn:fq:workflow-service-form-field:(urn:fq:workflow-service:system,condition)";

export const COMPARISON_OPTIONS: Record<Comparison, { text: string; allowedTypes?: Set<string> }> = {
    [Comparison.Equal]: {
        text: "equals",
    },
    [Comparison.LessThan]: {
        text: "less than",
        allowedTypes: new Set([FormFieldType.Date, FormFieldType.Time, FormFieldType.DateTime, FormFieldType.Number]),
    },
    [Comparison.LessThanEqual]: {
        text: "less than or equal",
        allowedTypes: new Set([FormFieldType.Date, FormFieldType.Time, FormFieldType.DateTime, FormFieldType.Number]),
    },
    [Comparison.GreaterThan]: {
        text: "greater than",
        allowedTypes: new Set([FormFieldType.Date, FormFieldType.Time, FormFieldType.DateTime, FormFieldType.Number]),
    },
    [Comparison.GreaterThanEqual]: {
        text: "greater than or equal",
        allowedTypes: new Set([FormFieldType.Date, FormFieldType.Time, FormFieldType.DateTime, FormFieldType.Number]),
    },
    [Comparison.NotEqual]: {
        text: "not equal",
    },
    [Comparison.Exists]: {
        text: "exists",
    },
    [Comparison.DoesntExist]: {
        text: "doesn't exist",
    },
    [Comparison.Contains]: {
        text: "contains",
        allowedTypes: new Set([
            FormFieldType.Text, FormFieldType.LongText, FormFieldType.Dropdown,
            FormFieldType.MultiLevelDropdown, FormFieldType.MultiSelectDropdown,
        ]),
    },
    [Comparison.DoesntContain]: {
        text: "doesn't contain",
        allowedTypes: new Set([
            FormFieldType.Text, FormFieldType.LongText, FormFieldType.Dropdown,
            FormFieldType.MultiLevelDropdown, FormFieldType.MultiSelectDropdown,
        ]),
    },
    [Comparison.Regexp]: {
        text: "regular expression",
        allowedTypes: new Set([
            FormFieldType.Text, FormFieldType.LongText, FormFieldType.Dropdown,
            FormFieldType.MultiLevelDropdown, FormFieldType.MultiSelectDropdown,
        ]),
    },
    [Comparison.In]: {
        text: "in",
        allowedTypes: new Set([]),
    },
};

const DEFAULT_CONDITIONS: WorkflowCondition[][] = [
    [
        {
            comparison: Comparison.Equal,
        },
    ],
];

export default function ConditionSettings(props: ActionProps): ReactElement {
    const theme = useTheme();
    const [lastValue, setValue] = useState<string>();
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [conditions, setConditions] = useState<WorkflowCondition[][]>(() => DEFAULT_CONDITIONS);
    const flatTypes = props.input.flatMap(row => row.input?.map(field => ({
        field,
        group: row,
    }))).filterNull();

    useEffect(() => {
        const serialized = JSON.stringify(conditions);
        setValue(serialized);
        const condition = conditions.firstElement.map(group => {
            const field = flatTypes.find(type => type.field.id === group.fieldId)?.field.name;
            const comparison = COMPARISON_OPTIONS[group.comparison as Comparison];
            return `${field} ${comparison.text} ${group.value?.displayValue ?? ""}`;
        }).join(" OR ");
        if (!isDirty) {
            return;
        }
        props.onChange({
            values: [
                {
                    fieldId: FORM_FIELD_ID,
                    type: FormFieldType.LongText,
                    displayValue: condition,
                    string: serialized,
                },
            ],
        });
    }, [conditions]);

    useEffect(() => {
        const stringValue = props.values?.[0]?.value?.string;
        if (!stringValue) {
            setConditions(DEFAULT_CONDITIONS);
        } else if (lastValue !== stringValue) {
            setConditions(JSON.parse(stringValue));
        }
    }, [props.values]);

    const addGroup = () => {
        const copy = Array.from(conditions);
        copy.push([
            {
                comparison: Comparison.Equal,
                fieldId: "",
            },
        ]);
        setIsDirty(true);
        setConditions(copy);
    };
    const addCondition = (groupIndex: number) => {
        const copy = Array.from(conditions);
        copy[groupIndex].push({
            comparison: Comparison.Equal,
            fieldId: "",
        });
        setIsDirty(true);
        setConditions(copy);
    };
    const deleteCondition = (groupIndex: number, conditionIndex: number) => {
        const copy = Array.from(conditions);
        copy[groupIndex].splice(conditionIndex, 1);
        if (copy[groupIndex].isEmpty) {
            copy.splice(groupIndex, 1);
        }
        if (copy.isEmpty) {
            copy.push([
                {
                    comparison: Comparison.Equal,
                    fieldId: "",
                },
            ]);
        }
        setIsDirty(true);
        setConditions(copy);
    };

    return (
        <div className="list-group list-flush">
            {conditions.map((conditionGroup, groupIndex) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`orGroup-and-${groupIndex}`}>
                    {groupIndex > 0 && (
                        <div
                            className={theme.isV3 ? Styles["condition-group-separator"] : "my-3 text-center"}
                        >
                            AND
                        </div>
                    )}
                    <div
                        className={classNames(theme.isV3 ? Styles["condition-group"] : "list-group-item bg-gray-200")}
                    >
                        {conditionGroup.map((condition, index) => {
                            const input = flatTypes.find(row => (!condition.stepId || condition.stepId === row.group.id)
                                && row.field.id === condition.fieldId);
                            const comparisons = Object.entries(COMPARISON_OPTIONS)
                                .filter(([, value]) => !value.allowedTypes
                                    || (input?.field.type && value.allowedTypes.has(input?.field.type)));
                            const isLast = index === conditionGroup.length - 1;
                            const isOnlyOne = conditions.length === 1 && conditionGroup.length === 1;
                            const selectedCondition = comparisons.find(([key]) => key === condition.comparison);
                            const conditionValue = condition?.value?.string as string;
                            const shouldUseEditor = props.shouldUseEditor && input?.field.type !== FormFieldType.Number;

                            return (
                                <div
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`condition-${groupIndex}-${index}`}
                                    className={theme.isV3 ? "d-flex" : `d-flex ${index > 0 ? "border-top pt-2 mt-2" : ""}`}
                                >
                                    <div className="col-3">
                                        <FieldSelector
                                            shouldExpandUsers
                                            onChange={value => {
                                                const copy = Array.from(conditions);
                                                if (value) {
                                                    copy[groupIndex][index].fieldId = value.field.id;
                                                    copy[groupIndex][index].stepId = value.group.id;
                                                }
                                                setIsDirty(true);
                                                setConditions(copy);
                                            }}
                                            value={input}
                                            input={props.input}
                                        />
                                    </div>
                                    <div className="col-3">
                                        {condition.fieldId && (
                                            <Select
                                                onChange={value => {
                                                    const copy = Array.from(conditions);
                                                    copy[groupIndex][index].comparison = value.value as Comparison;
                                                    setIsDirty(true);
                                                    setConditions(copy);
                                                }}
                                                value={!selectedCondition ? undefined : {
                                                    label: selectedCondition[1].text,
                                                    value: selectedCondition[0],
                                                }}
                                                options={comparisons.map(([value, options]) => ({
                                                    label: options.text,
                                                    value,
                                                }))}
                                            />
                                        )}

                                    </div>
                                    <div className="col">
                                        {input
                                            && condition.comparison !== Comparison.Exists
                                            && condition.comparison !== Comparison.DoesntExist
                                            && condition.comparison !== Comparison.Contains
                                            && condition.comparison !== Comparison.DoesntContain
                                            && condition.comparison !== Comparison.Regexp
                                            && (
                                                <FieldTypeValueDisplay
                                                    className=""
                                                    inputClassName="form-control"
                                                    field={input.field as FormField}
                                                    shouldShowFieldName={false}
                                                    value={condition.value as FieldTypedValue}
                                                    replaceableInput={props.input}
                                                    replacements={condition.replacement ?? []}
                                                    replacementInsertLabel=""
                                                    onChange={(value, replace) => {
                                                        const copy = Array.from(conditions);
                                                        copy[groupIndex][index].value = value;
                                                        copy[groupIndex][index].replacement = replace;
                                                        setIsDirty(true);
                                                        setConditions(copy);
                                                    }}
                                                    shouldUseEditor={shouldUseEditor}
                                                />
                                            )}

                                        {input && (
                                            condition.comparison === Comparison.Contains
                                            || condition.comparison === Comparison.DoesntContain
                                            || condition.comparison === Comparison.Regexp
                                        ) && (
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={conditionValue}
                                                onChange={evt => {
                                                    const copy = Array.from(conditions);
                                                    copy[groupIndex][index].value = {
                                                        displayValue: evt.target.value,
                                                        fieldId: input.field.id,
                                                        type: input.field.type,
                                                        string: evt.target.value,
                                                    };
                                                    setIsDirty(true);
                                                    setConditions(copy);
                                                }}
                                            />
                                        )}

                                    </div>
                                    <div className={theme.isV3
                                        ? Styles["condition-button-container"]
                                        : "col-1 d-flex justify-content-end"}
                                    >
                                        {isLast && (
                                            <button
                                                type="button"
                                                className={classNames("btn btn-none", theme.isV3 && Styles["condition-button"])}
                                                onClick={() => addCondition(groupIndex)}
                                            >
                                                OR
                                            </button>
                                        )}
                                        {!(isOnlyOne && isLast) && (
                                            theme.isV3 ? (
                                                <Button.Delete
                                                    onClick={() => deleteCondition(groupIndex, index)}
                                                    iconVariant="default"
                                                />
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="btn btn-none"
                                                    onClick={() => deleteCondition(groupIndex, index)}
                                                >
                                                    <FiTrash2 />
                                                </button>
                                            )
                                        )}

                                    </div>
                                </div>

                            );
                        })}
                    </div>
                </div>
            ))}
            <div className="">
                {
                    theme.isV3 ? (
                        <Button
                            variant="none"
                            onClick={addGroup}
                            className={Styles["add-condition-button"]}
                        >
                            ADD
                        </Button>
                    ) : (
                        <button type="button" className="btn btn-outline mt-2" onClick={addGroup}>
                            And
                        </button>
                    )
                }

            </div>
        </div>
    );
}
